import React from 'react';
import { Container, Typography, Box, Card, Grid, useTheme } from '@mui/material';
import { MoneyOff, Refresh, CheckCircle, Warning, Support, Email } from '@mui/icons-material';

const CancellationRefund = () => {
  const theme = useTheme();

  const guaranteeFeatures = [
    {
      icon: <CheckCircle sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "30-Day Money Back Guarantee",
      description: "If you don't find a match within 30 days of becoming a Premium Member, we'll refund your entire fee."
    },
    {
      icon: <Support sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "No Questions Asked",
      description: "Simple and straightforward refund process with no complicated procedures or hidden conditions."
    },
    {
      icon: <Refresh sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "Full Refund",
      description: "Complete refund of your membership fee if our guarantee conditions are met."
    }
  ];

  const requirements = [
    "Send at least 10 Interests to other members",
    "Complete your profile with all details",
    "Upload your photo",
    "Actively communicate with members",
    "No Accept responses within 30 days of Premium membership"
  ];

  const acceptedMembers = [
    "Members who expressed interest in you and you responded positively",
    "Members who responded positively to your expressed interest"
  ];

  const exclusions = [
    "Silver plan memberships",
    "Personalized memberships (VIP and Select memberships)",
    "Memberships with successful matches within 30 days"
  ];

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ 
        py: 10,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: 'white'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <MoneyOff sx={{ fontSize: 80, color: 'white' }} />
            </Box>
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Money Back Guarantee
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                opacity: 0.9
              }}
            >
              Millions of members have successfully found their matches. Our track record gives us the confidence to offer you this unique guarantee.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Guarantee Features */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            Our Guarantee
          </Typography>
          <Grid container spacing={4} wrap="nowrap" sx={{ overflowX: 'auto', pb: 2 }}>
            {guaranteeFeatures.map((feature, index) => (
              <Grid item xs={12} md={4} key={index} sx={{ minWidth: 320, maxWidth: 320 }}>
                <Card sx={{ 
                  p: 4,
                  textAlign: 'center',
                  height: '260px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                  }
                }}>
                  <Box sx={{ mb: 3 }}>
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 2
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      lineHeight: 1.6
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: 10, bgcolor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            How Our Money Back Guarantee Works
          </Typography>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                p: 4,
                minHeight: 280,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.07)',
                borderRadius: '18px',
                border: `1.5px solid ${theme.palette.primary.light}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.13)'
                }
              }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 3
                  }}
                >
                  What You Need to Do
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {requirements.map((requirement, index) => (
                    <Typography 
                      key={index}
                      component="li" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        lineHeight: 1.8,
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      {requirement}
                    </Typography>
                  ))}
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                p: 4,
                minHeight: 280,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.07)',
                borderRadius: '18px',
                border: `1.5px solid ${theme.palette.primary.light}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.13)'
                }
              }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 3
                  }}
                >
                  What Counts as an "Accept"
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    mb: 3
                  }}
                >
                  Accepted members are:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {acceptedMembers.map((member, index) => (
                    <Typography 
                      key={index}
                      component="li" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        lineHeight: 1.8,
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      {member}
                    </Typography>
                  ))}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Exclusions */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Card sx={{ 
            p: 4,
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: `2px solid ${theme.palette.warning.main}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Warning sx={{ fontSize: 40, color: theme.palette.warning.main, mr: 2 }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  color: theme.palette.warning.main
                }}
              >
                Important Exclusions
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                color: theme.palette.text.secondary,
                lineHeight: 1.8,
                mb: 3,
                fontSize: '1.1rem'
              }}
            >
              This 30-day money back guarantee is only applicable to online memberships (excluding the Silver plan) 
              and does not apply to our personalized memberships. Personalized membership includes VIP and Select memberships.
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              {exclusions.map((exclusion, index) => (
                <Typography 
                  key={index}
                  component="li" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    mb: 1,
                    fontSize: '1rem'
                  }}
                >
                  {exclusion}
                </Typography>
              ))}
            </Box>
          </Card>
        </Container>
      </Box>

      {/* Important Notes */}
      <Box sx={{ py: 10, bgcolor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            Important Notes
          </Typography>
          <Grid container spacing={4} wrap="nowrap" sx={{ overflowX: 'auto', pb: 2 }}>
            <Grid item xs={12} md={6} sx={{ minWidth: 320, maxWidth: 320 }}>
              <Card sx={{ 
                p: 4,
                height: '260px',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
                }
              }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 3
                  }}
                >
                  After Refund
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8
                  }}
                >
                  Premium features will no longer be available once a refund is issued. 
                  Your account will revert to basic membership status.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 320, maxWidth: 320 }}>
              <Card sx={{ 
                p: 4,
                height: '260px',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
                }
              }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 3
                  }}
                >
                  Our Success Rate
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8
                  }}
                >
                  Millions of members have successfully found their matches on our platform. 
                  Our track record of success gives us the confidence to offer this unique guarantee.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Card sx={{ 
            p: 4,
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Email sx={{ fontSize: 60, color: 'white', mb: 3 }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  mb: 3
                }}
              >
                Need Help?
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.8,
                  opacity: 0.9,
                  fontSize: '1.1rem',
                  mb: 3
                }}
              >
                If you have any questions about our money back guarantee or need assistance with the refund process, 
                please don't hesitate to contact us. We're here to help!
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  opacity: 0.9
                }}
              >
                Contact us at: support@matrimonial.com
              </Typography>
            </Box>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default CancellationRefund; 